import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import ProductionScheduleView, {
  type ProductionScheduleViewHandle,
  type ScheduleOperation,
  type ScheduleResource,
  type ScheduleResourceShift,
  type ScheduleSetup,
  type ZoomLevel
} from '../../components/ProductionScheduleView';
import type { ProductionScheduleRendererType } from '../../components/ProductionScheduleViewTypes';

/** Dialog-specific: includes capacityChart in addition to schedule/gantt. */
type VisualizationDialogRenderer = ProductionScheduleRendererType | 'capacityChart';
import { useTranslation } from 'react-i18next';
import { L10n } from '@syncfusion/ej2-base';
import type { ProductionScheduleDto } from '../../api/models/ProductionScheduleDto';
import type { CampaignData } from '../../api/models/CampaignData';
import type { ResourceDto } from '../../api/models/ResourceDto';
import type { AvailabilityDto } from '../../api/models/AvailabilityDto';
import type { CalculatedProductionPlanDto } from '../../api/models/CalculatedProductionPlanDto';
import type { OrderDto } from '../../api/models/OrderDto';
import type { SetupExecutionDto } from '../../types/SetupExecutionDto';
import { ResourceControllerService } from '../../api/services/ResourceControllerService';
import { ProductionScheduleControllerService } from '../../api/services/ProductionScheduleControllerService';
import { AvailabilityControllerService } from '../../api/services/AvailabilityControllerService';
import { useErrorHandler } from '../../utils/errorHandler';
import { GanttProfileSelector } from '../../components/GanttProfileSelector';
import { GanttVisualizationProfileControllerService, type GanttVisualizationProfileDto, type AdditionalResourceDto } from '../../api';
import ResourceUtilizationStepLineChart from '../../components/ResourceUtilizationStepLineChart';
import { profileResourcePredicate } from '../../utils/ganttProfileResourceFilter';
import { toLocalDateTimeString } from '../../utils/localDateTime';
import {
  parseSequenceSnapshot,
  buildSequenceGanttSlots,
  visibleOrderUuidsForPrefix,
  type SequenceGanttSlot
} from '../../utils/sequenceSnapshotGanttFilter';

// Interfaces
interface ProductionScheduleVisualizationDialogProps {
  schedule: ProductionScheduleDto;
  isOpen: boolean;
  onClose: () => void;
}


const ProductionScheduleVisualizationDialog: React.FC<ProductionScheduleVisualizationDialogProps> = ({
  schedule,
  isOpen,
  onClose
}) => {
  const { t, i18n } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [planDataLoading, setPlanDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resources, setResources] = useState<ResourceDto[]>([]);
  const [planData, setPlanData] = useState<CalculatedProductionPlanDto | null>(null);
  const [availabilities, setAvailabilities] = useState<AvailabilityDto[]>([]);
  const scheduleViewRef = useRef<ProductionScheduleViewHandle>(null);
  const [selectedProfile, setSelectedProfile] = useState<GanttVisualizationProfileDto | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [renderer, setRenderer] = useState<VisualizationDialogRenderer>('schedule');
  const [capacityChartResourceId, setCapacityChartResourceId] = useState<string | null>(null);
  
  // Zoom levels: passen zu ProductionScheduleView ZoomLevel (5, 10, 15, 30, 60, 120 Minuten)
  const zoomLevels: Array<{ level: ZoomLevel; label: string }> = [
    { level: '5min', label: '5 Min' },
    { level: '10min', label: '10 Min' },
    { level: '15min', label: '15 Min' },
    { level: '30min', label: '30 Min' },
    { level: '60min', label: '1 Std' },
    { level: '120min', label: '2 Std' }
  ];
  const [zoomLevelIndex, setZoomLevelIndex] = useState(1); // Default: 10min (index 1)
  const zoomLevelIndexRef = useRef<number>(0);
  /** 'all' | '1'... = cumulative number of sequence slots */
  const [sequenceFilterValue, setSequenceFilterValue] = useState<string>('all');
  
  // Keep ref in sync with state
  useEffect(() => {
    zoomLevelIndexRef.current = zoomLevelIndex;
  }, [zoomLevelIndex]);

  useEffect(() => {
    setSequenceFilterValue('all');
  }, [schedule?.uuid, planData?.planUuid, planData?.calculatedAt]);

  // Color palette for resources
  const resourceColors = useMemo(() => [
    '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3',
    '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43', '#10ac84',
    '#ee5a24', '#ff3838', '#ff6348', '#ff9ff3', '#54a0ff'
  ], []);

  // Track if we've already loaded data for this schedule UUID
  const loadedScheduleUuidRef = useRef<string | null>(null);
  const loadedPlanDataRef = useRef<string | null>(null);
  
  // Reset planData when schedule changes
  useEffect(() => {
    if (schedule?.uuid && loadedScheduleUuidRef.current !== schedule.uuid) {
      setPlanData(null);
    }
  }, [schedule?.uuid]);

  // Fetch resources
  const fetchResources = useCallback(async () => {
    setResourcesLoading(true);
    try {
      const data = await ResourceControllerService.findAll();
      setResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to load resources: ${errorMessage}`);
      handleError(error, 'loading resources');
    } finally {
      setResourcesLoading(false);
    }
  }, [handleError]);

  const [campaigns, setCampaigns] = useState<any[]>([]);

  // Fetch campaigns for KPIs
  const fetchCampaigns = useCallback(async () => {
    if (!schedule?.uuid) {
      return;
    }

    try {
      const campaignsData = await ProductionScheduleControllerService.getCampaigns(schedule.uuid);
      setCampaigns(campaignsData || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      // Fallback: use campaigns from schedule prop if available
      if (schedule?.campaigns) {
        setCampaigns(schedule.campaigns);
      } else {
        setCampaigns([]);
      }
    }
  }, [schedule?.uuid, schedule?.campaigns]);

  // Fetch production schedule data for KPIs and executions
  const fetchPlanData = useCallback(async () => {
    if (!schedule?.uuid) {
      return;
    }

    // Prevent multiple calls for the same schedule
    if (loadedPlanDataRef.current === schedule.uuid) {
      return;
    }

    loadedPlanDataRef.current = schedule.uuid;
    setPlanDataLoading(true);
    try {
      const response = await ProductionScheduleControllerService.getCalculatedProductionScheduleById(schedule.uuid);
      setPlanData(response);
    } catch (error) {
      console.error('Error fetching plan data:', error);
      // Don't set error state, just log it - KPIs are optional
    } finally {
      setPlanDataLoading(false);
    }
  }, [schedule?.uuid]);

  // Order: 1) load the visualization profile, 2) then load and build the diagram data
  useEffect(() => {
    if (!isOpen || !schedule?.uuid) {
      if (!isOpen) {
        loadedScheduleUuidRef.current = null;
        loadedPlanDataRef.current = null;
        setProfileLoading(true);
      }
      return;
    }

    let cancelled = false;

    const init = async () => {
      setProfileLoading(true);
      setSelectedProfile(null);
      try {
        const defaultProfile = await GanttVisualizationProfileControllerService.getDefaultProfile();
        if (cancelled) return;
        if (defaultProfile?.uuid) {
          setSelectedProfile(defaultProfile);
        } else {
          const profiles = await GanttVisualizationProfileControllerService.findAll();
          if (cancelled) return;
          if (profiles?.length) {
            setSelectedProfile(profiles[0]);
          }
        }
      } catch (e) {
        console.error('Failed to load visualization profile:', e);
        try {
          const profiles = await GanttVisualizationProfileControllerService.findAll();
          if (!cancelled && profiles?.length) setSelectedProfile(profiles[0]);
        } catch {
          // ignore
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }

      if (cancelled) return;
      const scheduleUuid = schedule.uuid ?? null;
      if (loadedScheduleUuidRef.current === scheduleUuid) return;
      loadedScheduleUuidRef.current = scheduleUuid;
      loadedPlanDataRef.current = null;
      fetchResources();
      fetchPlanData();
      fetchCampaigns();
    };

    init();
    return () => { cancelled = true; };
  }, [isOpen, schedule?.uuid, fetchResources, fetchPlanData, fetchCampaigns]);

  // Fetch availabilities for shift background rendering

  // Filter resources based on profile settings
  const isResourceVisible = useMemo(() => profileResourcePredicate(selectedProfile), [selectedProfile]);
  const filteredResources = useMemo(
    () => resources.filter(isResourceVisible),
    [resources, isResourceVisible]
  );

  const resourceShiftsById = useMemo(() => {
    const map = new Map<string, ScheduleResourceShift[]>();
    availabilities.forEach(availability => {
      if (!availability.resourceId) return;
      if (!availability.startDateTime || !availability.endDateTime) return;
      if (availability.type !== 'OFF_SHIFT') return;

      const startTime = new Date(availability.startDateTime);
      let endTime = new Date(availability.endDateTime);
      if (endTime < startTime) {
        endTime = new Date(endTime.getTime() + 24 * 60 * 60 * 1000);
      }

      const shift: ScheduleResourceShift = {
        start: startTime,
        end: endTime,
        label: availability.shiftName || availability.reason,
        type: 'off'
      };

      const list = map.get(availability.resourceId) ?? [];
      list.push(shift);
      map.set(availability.resourceId, list);
    });

    map.forEach(list =>
      list.sort((a, b) => a.start.getTime() - b.start.getTime())
    );
    return map;
  }, [availabilities]);

  // Transform resources to scheduler format with profile-based colors
  const schedulerResources = useMemo((): ScheduleResource[] => {
    const colorConfig = selectedProfile?.colorConfiguration as any;
    const colorStrategy = colorConfig?.colorStrategy || 'PRODUCT';
    const resourceColorMappings = colorConfig?.resourceColors || [];

    const transformed = filteredResources.map((resource, index) => {
      // Determine color based on strategy
      let color = resourceColors[index % resourceColors.length];
      
      if (colorStrategy === 'RESOURCE' && resourceColorMappings.length > 0) {
        const mapping = resourceColorMappings.find((m: any) => m.uuid === resource.uuid);
        if (mapping) {
          if (mapping.colorMode === 'MANUAL' && mapping.color) {
            color = mapping.color;
          } else if (mapping.colorMode === 'AUTO' && mapping.autoColorIndex !== undefined) {
            color = resourceColors[mapping.autoColorIndex % resourceColors.length];
          }
        }
      }

      return {
        id: resource.uuid || '0',
        name: resource.name || 'Unknown Resource',
        type: resource.resourceTypeName || 'Unknown Type',
        color: color,
        shifts: resource.uuid ? resourceShiftsById.get(resource.uuid) ?? [] : []
      };
    });
    return transformed;
  }, [filteredResources, selectedProfile, resourceColors, resourceShiftsById]);

  // Create a virtual "Unassigned" resource for executions without resource
  const unassignedResourceId = 'unassigned-resource';
  const schedulerResourcesWithUnassigned = useMemo(() => {
    const base = [...schedulerResources];
    const idSet = new Set(base.map(r => String(r.id)));

    // Merge in resources from planData that are referenced in assignments but missing from the
    // resource list (tenant mismatch). Resources the profile filter excludes stay hidden.
    const assignedResourceUuids = new Set(
      (planData?.operationAssignments ?? [])
        .map(a => (a.resourceUuid != null ? String(a.resourceUuid) : null))
        .filter((u): u is string => u != null)
    );
    const setupResourceUuids = new Set(
      (planData?.setupExecutions ?? [])
        .map(s => (s.resourceUuid != null ? String(s.resourceUuid) : null))
        .filter((u): u is string => u != null)
    );
    const additionalResourceUuids = new Set(
      (planData?.additionalResources ?? [])
        .map((ar: { resourceUuid?: string }) => (ar.resourceUuid != null ? String(ar.resourceUuid) : null))
        .filter((u): u is string => u != null)
    );
    const neededUuids = new Set([...assignedResourceUuids, ...setupResourceUuids, ...additionalResourceUuids]);

    (planData?.resources ?? []).forEach((pr: any, index: number) => {
      const uuid = pr.uuid != null ? String(pr.uuid) : null;
      if (uuid && neededUuids.has(uuid) && !idSet.has(uuid) && isResourceVisible(pr)) {
        idSet.add(uuid);
        base.push({
          id: uuid,
          name: pr.name ?? 'Unknown',
          type: pr.resourceType ?? pr.resourceTypeName ?? '',
          color: resourceColors[(schedulerResources.length + index) % resourceColors.length]
        });
      }
    });

    // Add unassigned resource if any assignments lack resourceUuid
    const hasUnassigned = planData?.operationAssignments?.some(
      a => !a.resourceUuid && a.plannedStart && a.plannedEnd
    );
    if (hasUnassigned && !idSet.has(unassignedResourceId)) {
      base.push({
        id: unassignedResourceId,
        name: t('common.unassigned', 'Nicht zugewiesen'),
        type: 'Unassigned',
        color: '#9ca3af'
      });
    }
    return base;
  }, [schedulerResources, planData, t, resourceColors, isResourceVisible]);

  // Transform operation assignments to scheduler events with profile-based colors
  const operations = useMemo((): ScheduleOperation[] => {
    if (!planData?.operationAssignments || planData.operationAssignments.length === 0) {
      return [];
    }

    const campaignsData = campaigns.length > 0 ? campaigns : (schedule?.campaigns || []);
    const campaignIdByMaterialId = new Map<string, string>();
    campaignsData.forEach((campaign: any) => {
      if (campaign?.materialId && campaign?.campaignId && !campaignIdByMaterialId.has(campaign.materialId)) {
        campaignIdByMaterialId.set(campaign.materialId, campaign.campaignId);
      }
    });

    const transformed: ScheduleOperation[] = [];
    let eventIdCounter = 1;

    // Get color configuration from profile
    const colorConfig = selectedProfile?.colorConfiguration as any;
    const colorStrategy = colorConfig?.colorStrategy || 'PRODUCT';
    const productColorMappings = colorConfig?.productColors || [];
    const resourceColorMappings = colorConfig?.resourceColors || [];

    // Color palette for production orders/products (one color per order/material)
    const orderColorMap = new Map<string, string>();
    const productColorMap = new Map<string, string>();
    const resourceColorMap = new Map<string, string>();
    const orderColors = [
      '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3',
      '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43', '#10ac84',
      '#ee5a24', '#ff3838', '#ff6348', '#ff9ff3', '#54a0ff'
    ];
    let colorIndex = 0;

    // Create a map from operationUuid to orderUuid
    // IMPORTANT: The backend uses findFirst() which is wrong - all operations get the same orderUuid
    // So we need to create a better mapping by going through assignments and finding the correct order
    // Strategy: Each assignment (execution) belongs to one operation and one order
    // We can create a map by going through all assignments and finding which order each operation belongs to
    
    // First, create a map from assignmentUuid to orderUuid by matching assignments with orders
    // Since assignments are executions, and executions have productionOrder, we need to find this relationship
    // But OperationAssignmentDto doesn't have orderUuid, so we need to infer it
    
    // Better approach: Create a map from operationUuid to orderUuid by finding which order contains this operation
    // Each order has operations, so we can match operations to orders
    const normId = (u: string | undefined | null) =>
      u != null && String(u).trim() !== '' ? String(u).trim().toLowerCase() : '';

    const operationToOrderMap = new Map<string, string>();

    // orderUuid from operations - normalize keys/values (otherwise orderMap.get fails)
    planData.operations?.forEach(op => {
      const opKey = normId(op.uuid);
      const orderUuid = (op as { orderUuid?: string }).orderUuid;
      if (opKey && orderUuid) {
        operationToOrderMap.set(opKey, normId(orderUuid));
      }
    });
    
    // Now, improve the mapping by checking if operations actually belong to the orders they're mapped to
    // If we have multiple orders with different materials, we can use that to correct the mapping
    // For each assignment, find the operation and check if the mapped order is correct
    // If not, try to find the correct order by matching operation names or other criteria
    
    // Actually, a simpler approach: Since each execution (assignment) belongs to one operation and one order,
    // and we have the operationUuid in the assignment, we can create a reverse mapping
    // But we don't have orderUuid in assignments, so we need to infer it
    
    // Best approach: Use the fact that operations belong to orders, and we can match them
    // by checking which order's operations list contains this operation
    // But we don't have operations list in OrderDto
    
    // Final approach: Use materialUuid to distinguish orders if they have different materials
    // If all operations have the same orderUuid but there are multiple orders with different materials,
    // we can use the materialUuid from the order to create a better mapping
    const orderMaterialMap = new Map<string, string>(); // orderUuid (lower) -> materialUuid
    planData.orders?.forEach(order => {
      const ou = normId(order.orderUuid);
      if (ou && order.materialUuid) {
        orderMaterialMap.set(ou, order.materialUuid);
      }
    });

    const orderMap = new Map<string, OrderDto>();
    planData.orders?.forEach(order => {
      const ou = normId(order.orderUuid);
      if (ou) {
        orderMap.set(ou, order);
      }
    });

    // Alternative: Create a map from operationUuid to materialUuid via assignments
    // This helps when orderUuid is not set correctly
    const operationToMaterialMap = new Map<string, string>();
    planData.operationAssignments?.forEach(assignment => {
      const assignmentOperationUuid = normId(assignment.operationUuid);
      if (!assignmentOperationUuid) {
        return;
      }

      const operation = planData.operations?.find(op => normId(op.uuid) === assignmentOperationUuid);
      if (operation) {
        const orderUuid = operationToOrderMap.get(assignmentOperationUuid);
        const order = orderUuid ? orderMap.get(orderUuid) : undefined;
        if (order?.materialUuid) {
          operationToMaterialMap.set(assignmentOperationUuid, order.materialUuid);
        }
      }
    });

    if (planData.operationAssignments && planData.operationAssignments.length > 0) {
    planData.operationAssignments.forEach((assignment) => {
      const assignmentOperationUuid = normId(assignment.operationUuid);
      if (!assignmentOperationUuid) {
        return;
      }

      // Skip assignments without dates
      if (!assignment.plannedStart || !assignment.plannedEnd) {
        return;
      }

      // Use unassigned resource if no resourceUuid
      const resourceUuid = assignment.resourceUuid != null ? String(assignment.resourceUuid) : unassignedResourceId;
      
      // Find the resource to get its name; include if in our display list (filtered + planData.resources)
      const resource = schedulerResourcesWithUnassigned.find(r => String(r.id) === resourceUuid);
      if (!resource) {
        // Skip if resource is not in filtered list
        return;
      }

      const operation = planData.operations?.find(op => normId(op.uuid) === assignmentOperationUuid);
      if (!operation) {
        return;
      }

      const operationOrderUuid = operationToOrderMap.get(assignmentOperationUuid);
      let order = operationOrderUuid ? orderMap.get(operationOrderUuid) : undefined;
      
      // Fallback: Try to find order by materialUuid if we have it
      if (!order) {
        const materialUuid = operationToMaterialMap.get(assignmentOperationUuid);
        if (materialUuid) {
          order = planData.orders?.find(o => o.materialUuid === materialUuid);
          // order found via materialUuid lookup
        }
      }
      
      // Last fallback: Use first order if available
      if (!order && planData.orders && planData.orders.length > 0) {
        order = planData.orders[0];
        console.warn(`No orderUuid found for operation ${assignmentOperationUuid}, using first order as fallback`);
      }
      
      if (!order) {
        return;
      }

      // Get or assign color based on profile color strategy
      let categoryColor: string;
      
      if (colorStrategy === 'PRODUCT') {
        // Color by product/material
        // Use materialUuid from order, or try to get it from operationToMaterialMap
        let productUuid = order.materialUuid;
        if (!productUuid) {
          productUuid = operationToMaterialMap.get(assignmentOperationUuid) || 'unknown';
        }
        
        
        if (!productColorMap.has(productUuid)) {
          // Check if there's a manual mapping
          const mapping = productColorMappings.find((m: any) => m.uuid === productUuid);
          if (mapping && mapping.colorMode === 'MANUAL' && mapping.color) {
            productColorMap.set(productUuid, mapping.color);
          } else if (mapping && mapping.colorMode === 'AUTO' && mapping.autoColorIndex !== undefined) {
            const color = orderColors[mapping.autoColorIndex % orderColors.length];
            productColorMap.set(productUuid, color);
          } else {
            const color = orderColors[colorIndex % orderColors.length];
            productColorMap.set(productUuid, color);
            colorIndex++;
          }
        }
        categoryColor = productColorMap.get(productUuid)!;
      } else if (colorStrategy === 'ORDER') {
        // Color by production order (status-based or order-based)
        const orderId = order.orderUuid || order.orderNumber || 'unknown';
        if (!orderColorMap.has(orderId)) {
          // For ORDER strategy, we could use status-based colors
          // For now, use order-based colors
          orderColorMap.set(orderId, orderColors[colorIndex % orderColors.length]);
          colorIndex++;
        }
        categoryColor = orderColorMap.get(orderId)!;
      } else if (colorStrategy === 'RESOURCE') {
        // Color by resource
        const resourceUuid = assignment.resourceUuid || unassignedResourceId;
        if (!resourceColorMap.has(resourceUuid)) {
          const mapping = resourceColorMappings.find((m: any) => m.uuid === resourceUuid);
          if (mapping && mapping.colorMode === 'MANUAL' && mapping.color) {
            resourceColorMap.set(resourceUuid, mapping.color);
          } else if (mapping && mapping.colorMode === 'AUTO' && mapping.autoColorIndex !== undefined) {
            resourceColorMap.set(resourceUuid, orderColors[mapping.autoColorIndex % orderColors.length]);
          } else {
            // Find resource in schedulerResources to get its color
            const resource = schedulerResourcesWithUnassigned.find(r => String(r.id) === resourceUuid);
            resourceColorMap.set(resourceUuid, resource?.color || orderColors[colorIndex % orderColors.length]);
            colorIndex++;
          }
        }
        categoryColor = resourceColorMap.get(resourceUuid)!;
      } else {
        // Fallback to order-based coloring
        const orderId = order.orderUuid || order.orderNumber || 'unknown';
        if (!orderColorMap.has(orderId)) {
          orderColorMap.set(orderId, orderColors[colorIndex % orderColors.length]);
          colorIndex++;
        }
        categoryColor = orderColorMap.get(orderId)!;
      }

      // Parse dates (API sends LocalDateTime as ISO without Z → parsed as local time)
      const startTime = new Date(assignment.plannedStart);
      let endTime = new Date(assignment.plannedEnd);

      // Workaround: for an end time of exactly 00:00:00 the Syncfusion Schedule can compute the duration
      // as '1 day + remainder' (24h 1min instead of 1min). 00:00 belongs to the new day (start of day).
      // When the end is midnight and the duration is < 24h: move the end back by 1ms (23:59:59.999)
      // so that the display is correct; semantically 'until 20.02. 00:00' is preserved.
      const durationMs = endTime.getTime() - startTime.getTime();
      const isEndAtMidnight = endTime.getHours() === 0 && endTime.getMinutes() === 0 && endTime.getSeconds() === 0 && endTime.getMilliseconds() === 0;
      if (isEndAtMidnight && durationMs > 0 && durationMs < 24 * 60 * 60 * 1000) {
        endTime = new Date(endTime.getTime() - 1);
      }

      // Get operation name (we already have the operation from above)
      const operationName = operation.name || 'Operation';
      const orderName = order.orderNumber || 'Order';
      const productName = order.materialName || order.orderName || order.orderNumber || '';

      // Create event
      // IMPORTANT: Resources must be an array of resource IDs that match the resourceData.id values
      const campaignId =
        order.campaignId ||
        (order.campaignUuid != null ? String(order.campaignUuid) : undefined) ||
        (assignment as any)?.campaignId ||
        (assignment as any)?.campaignUuid ||
        (order?.materialUuid ? campaignIdByMaterialId.get(order.materialUuid) : undefined);

      const event: ScheduleOperation = {
        id: eventIdCounter++,
        subject: `${orderName} - ${operationName}`,
        operationName: operationName,
        campaignId: campaignId,
        startTime: startTime,
        endTime: endTime,
        resourceId: resourceUuid,
        color: categoryColor,
        orderUuid: order?.orderUuid != null ? String(order.orderUuid).trim().toLowerCase() : undefined,
        orderNumber: order?.orderNumber,
        productName: productName
      };
      
      // Verify resourceUuid exists in schedulerResourcesWithUnassigned
      if (!schedulerResourcesWithUnassigned.some(r => r.id === resourceUuid)) {
        console.error('Event created with resourceUuid not in schedulerResourcesWithUnassigned:', {
          resourceUuid,
          eventId: event.id,
          availableResourceIds: schedulerResourcesWithUnassigned.map(r => r.id)
        });
      }

      transformed.push(event);
    });
    }

    return transformed;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- campaigns and schedule?.campaigns are derived from planData; adding them would cause redundant recalculations
  }, [planData, schedulerResourcesWithUnassigned, unassignedResourceId, selectedProfile, t]);

  // Setups from setupExecutions + secondary/conditional resources from additionalResources
  const setups = useMemo((): ScheduleSetup[] => {
    const setupColor = '#000000';
    const secondaryColorFallback = '#7c3aed'; // fallback when product is not set
    const orderColors = [
      '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3',
      '#54a0ff', '#5f27cd', '#00d2d3', '#ff9f43', '#10ac84',
      '#ee5a24', '#ff3838', '#ff6348', '#ff9ff3', '#54a0ff'
    ];
    let colorIndex = 0;
    const colorConfig = selectedProfile?.colorConfiguration as { productColors?: Array<{ uuid?: string; colorMode?: string; color?: string; autoColorIndex?: number }> } | undefined;
    const productColorMappings = colorConfig?.productColors ?? [];
    const productColorMap = new Map<string, string>();
    planData?.orders?.forEach(order => {
      const productUuid = order.materialUuid != null ? String(order.materialUuid) : null;
      if (!productUuid || productColorMap.has(productUuid)) return;
      const mapping = productColorMappings.find((m: { uuid?: string }) => m.uuid === productUuid);
      if (mapping?.colorMode === 'MANUAL' && mapping.color) {
        productColorMap.set(productUuid, mapping.color);
      } else if (mapping?.colorMode === 'AUTO' && mapping.autoColorIndex !== undefined) {
        productColorMap.set(productUuid, orderColors[mapping.autoColorIndex % orderColors.length]);
      } else {
        productColorMap.set(productUuid, orderColors[colorIndex % orderColors.length]);
        colorIndex++;
      }
    });

    let idCounter = 1;
    const result: ScheduleSetup[] = [];

    const setupExecutions = planData?.setupExecutions ?? [];
    setupExecutions.forEach((se: SetupExecutionDto) => {
      if (!se.plannedStart || !se.plannedEnd || !se.resourceUuid) return;
      const resource = schedulerResourcesWithUnassigned.find(r => String(r.id) === String(se.resourceUuid));
      if (!resource) return;
      const startTime = new Date(se.plannedStart);
      let endTime = new Date(se.plannedEnd);
      const durationMs = endTime.getTime() - startTime.getTime();
      const isEndAtMidnight = endTime.getHours() === 0 && endTime.getMinutes() === 0 && endTime.getSeconds() === 0 && endTime.getMilliseconds() === 0;
      if (isEndAtMidnight && durationMs > 0 && durationMs < 24 * 60 * 60 * 1000) {
        endTime = new Date(endTime.getTime() - 1);
      }
      const setupTypeLabel = se.setupType || 'SETUP';
      const subject = se.notes
        ? `${t('productionSchedule.visualization.setupTime', 'Rüstzeit')}: ${se.notes}`
        : `${t('productionSchedule.visualization.setupTime', 'Rüstzeit')} (${setupTypeLabel})`;
      const pre = se.precedingOrderUuid != null ? String(se.precedingOrderUuid).trim().toLowerCase() : '';
      const succ = se.succeedingOrderUuid != null ? String(se.succeedingOrderUuid).trim().toLowerCase() : '';
      result.push({
        id: idCounter++,
        subject,
        startTime,
        endTime,
        resourceId: se.resourceUuid != null ? String(se.resourceUuid) : '',
        color: setupColor,
        precedingOrderUuid: pre || undefined,
        succeedingOrderUuid: succ || undefined,
        isAdditionalResourceBar: false
      });
    });

    // Secondary and conditional resource requirements (additionalResources) - color by product, product name for the tooltip
    const additionalResources = planData?.additionalResources ?? [];
    additionalResources.forEach((ar: AdditionalResourceDto) => {
      const type = (ar.resourceRequirementType ?? '').toUpperCase();
      if (type !== 'SECONDARY' && type !== 'CONDITIONAL') return;
      if (!ar.timeWindowStart || !ar.timeWindowEnd || !ar.resourceUuid) return;
      const resource = schedulerResourcesWithUnassigned.find(r => String(r.id) === String(ar.resourceUuid));
      if (!resource) return;
      const startTime = new Date(ar.timeWindowStart);
      const endTime = new Date(ar.timeWindowEnd);
      if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime()) || endTime <= startTime) return;
      const label = type === 'CONDITIONAL'
        ? t('productionSchedule.visualization.conditionalResource', 'Konditional')
        : t('productionSchedule.visualization.secondaryResource', 'Sekundär');
      const materialUuid = ar.materialUuid != null ? String(ar.materialUuid) : null;
      const color = materialUuid && productColorMap.has(materialUuid)
        ? productColorMap.get(materialUuid)!
        : secondaryColorFallback;
      const subject = `${label}: ${ar.resourceName ?? resource.name}`;
      result.push({
        id: `ar-${idCounter++}`,
        subject,
        startTime,
        endTime,
        resourceId: String(ar.resourceUuid),
        color,
        productName: ar.materialName ?? undefined,
        isAdditionalResourceBar: true,
        materialUuid: materialUuid ?? undefined
      });
    });

    return result;
  }, [planData, schedulerResourcesWithUnassigned, selectedProfile, t]);

  const sequenceSlots = useMemo((): SequenceGanttSlot[] => {
    if (!planData) return [];
    const campaignHints = (campaigns.length > 0 ? campaigns : schedule?.campaigns ?? []) as CampaignData[];
    return buildSequenceGanttSlots(
      parseSequenceSnapshot(schedule.sequenceSnapshot),
      planData,
      planData.followUpOrderUuids,
      { special: (n) => t('productionSchedule.visualization.specialOrders', { count: n }) },
      campaignHints
    );
  }, [schedule.sequenceSnapshot, schedule?.campaigns, planData, t, campaigns]);

  const sequenceFilterHasSlots = sequenceSlots.length > 0;

  const sequenceFilterDataSource = useMemo(() => {
    const items: { text: string; value: string }[] = [
      { text: t('productionSchedule.visualization.sequenceFilterAll'), value: 'all' }
    ];
    sequenceSlots.forEach((slot, i) => {
      items.push({
        text: t('productionSchedule.visualization.sequenceSlotOption', {
          order: i + 1,
          label: slot.label
        }),
        value: String(i + 1)
      });
    });
    return items;
  }, [sequenceSlots, t]);

  const sequenceVisibleOrderSet = useMemo(() => {
    if (!sequenceFilterHasSlots || sequenceFilterValue === 'all' || !planData) {
      return null as Set<string> | null;
    }
    const k = Number.parseInt(sequenceFilterValue, 10);
    if (!Number.isFinite(k) || k < 1) {
      return null;
    }
    const set = visibleOrderUuidsForPrefix(sequenceSlots, k);
    if (
      set.size === 0 &&
      (planData.orders?.length ?? 0) > 0 &&
      planData.operationAssignments &&
      planData.operationAssignments.length > 0
    ) {
      console.warn(
        '[Gantt sequence filter] Präfix ergibt keine Auftrags-UUIDs – Zuordnung Snapshot/Plan fehlt; Filter deaktiviert.',
        {
          prefixSlots: k,
          slotSizes: sequenceSlots.map((s) => ({ kind: s.kind, orderCount: s.orderUuids.size }))
        }
      );
      return null;
    }
    return set;
  }, [sequenceFilterHasSlots, sequenceFilterValue, sequenceSlots, planData]);

  const filteredOperations = useMemo(() => {
    if (sequenceVisibleOrderSet == null) return operations;
    return operations.filter(op => {
      const ou = op.orderUuid != null ? String(op.orderUuid).trim().toLowerCase() : '';
      return ou !== '' && sequenceVisibleOrderSet.has(ou);
    });
  }, [operations, sequenceVisibleOrderSet]);

  const filteredSetups = useMemo(() => {
    if (sequenceVisibleOrderSet == null) return setups;
    const visMat = new Set<string>();
    for (const id of sequenceVisibleOrderSet) {
      const o = planData?.orders?.find(ord => String(ord.orderUuid).trim().toLowerCase() === id);
      if (o?.materialUuid) {
        visMat.add(String(o.materialUuid).trim().toLowerCase());
      }
    }
    return setups.filter(s => {
      if (s.isAdditionalResourceBar) {
        const m = s.materialUuid != null ? String(s.materialUuid).trim().toLowerCase() : '';
        if (!m) return false;
        return visMat.has(m);
      }
      const pre = s.precedingOrderUuid != null ? String(s.precedingOrderUuid).trim().toLowerCase() : '';
      const succ = s.succeedingOrderUuid != null ? String(s.succeedingOrderUuid).trim().toLowerCase() : '';
      if (pre && succ) {
        return sequenceVisibleOrderSet.has(pre) && sequenceVisibleOrderSet.has(succ);
      }
      return false;
    });
  }, [setups, sequenceVisibleOrderSet, planData?.orders]);

  const timeWindow = useMemo(() => {
    const plannedStart = schedule?.plannedStart ? new Date(schedule.plannedStart) : null;
    const plannedEnd = schedule?.plannedEnd ? new Date(schedule.plannedEnd) : null;

    const allStarts = [...operations.map(o => o.startTime.getTime()), ...setups.map(s => s.startTime.getTime())];
    const allEnds = [...operations.map(o => o.endTime.getTime()), ...setups.map(s => s.endTime.getTime())];
    const fallbackStart = allStarts.length > 0 ? new Date(Math.min(...allStarts)) : plannedStart;
    const fallbackEnd = allEnds.length > 0 ? new Date(Math.max(...allEnds)) : plannedEnd;

    const start = plannedStart && fallbackStart
      ? new Date(Math.min(plannedStart.getTime(), fallbackStart.getTime()))
      : plannedStart ?? fallbackStart;
    const end = plannedEnd && fallbackEnd
      ? new Date(Math.max(plannedEnd.getTime(), fallbackEnd.getTime()))
      : plannedEnd ?? fallbackEnd;

    if (!start || !end || end <= start) {
      throw new Error(
        'TimeWindow kann nicht ermittelt werden: Schedule hat weder plannedStart/plannedEnd noch Operationen mit Zeiten.'
      );
    }
    return { start, end };
  }, [operations, setups, schedule?.plannedEnd, schedule?.plannedStart]);

  /** For the resource utilization: shorten the timeline to the last delivery time (campaign dueDate). */
  const capacityChartTimeWindow = useMemo(() => {
    const campaignsData = campaigns.length > 0 ? campaigns : (schedule?.campaigns || []);
    const dueDates = campaignsData
      .map((c: { dueDate?: string }) => c?.dueDate)
      .filter(Boolean) as string[];
    if (dueDates.length === 0) return timeWindow;
    const latestDueDateStr = dueDates.reduce((latest, d) => (d > latest ? d : latest), '');
    const endOfDueDay = new Date(latestDueDateStr);
    if (!latestDueDateStr.includes('T')) {
      endOfDueDay.setHours(23, 59, 59, 999);
    }
    const cappedEnd = timeWindow.end > endOfDueDay ? endOfDueDay : timeWindow.end;
    if (cappedEnd <= timeWindow.start) return timeWindow;
    return { start: timeWindow.start, end: cappedEnd };
  }, [timeWindow, campaigns, schedule?.campaigns]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- isOpen intentionally used to refresh `now` when dialog opens
  const now = useMemo(() => new Date(), [isOpen]);

  const availStartMs = timeWindow.start.getTime();
  const availEndMs = timeWindow.end.getTime();
  useEffect(() => {
    if (!isOpen) {
      setAvailabilities([]);
      return;
    }

    // Local wall clock for the LocalDateTime range (review round 5, finding 2.8)
    const startDateStr = toLocalDateTimeString(timeWindow.start);
    const endDateStr = toLocalDateTimeString(timeWindow.end);
    let cancelled = false;

    const loadAvailabilities = async () => {
      try {
        const data = await AvailabilityControllerService.getAllAvailabilities(
          startDateStr,
          endDateStr
        ) as AvailabilityDto[];
        if (!cancelled) {
          setAvailabilities(data || []);
        }
      } catch (error) {
        handleError(error, 'loading availabilities');
        if (!cancelled) {
          setAvailabilities([]);
        }
      }
    };

    void loadAvailabilities();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- timeWindow tracked via primitive timestamps; handleError may not be stable
  }, [handleError, isOpen, availStartMs, availEndMs]);

  useEffect(() => {
    if (!resourcesLoading && !planDataLoading && operations.length > 0) {
      const timeoutId = setTimeout(() => {
        scheduleViewRef.current?.scrollToStart();
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [operations.length, planDataLoading, resourcesLoading]);

  const handleClose = () => {
    setResources([]);
    setResourcesLoading(true);
    setError(null);
    onClose();
  };

  // Store current scroll position (left edge) before zooming
  // COMMENTED OUT: Custom zoom scroll position logic
  // const storeScrollPosition = () => {
  //   if (scheduleRef.current) {
  //     const scheduleElement = scheduleRef.current.element;
  //     const contentArea = scheduleElement?.querySelector('.e-content-wrap') || scheduleElement?.querySelector('.e-timeline-view');
  //     
  //     if (contentArea) {
  //       const scrollLeft = contentArea.scrollLeft;
  //       
  //       // Store the left edge position in pixels
  //       scrollPositionRef.current = {
  //         scrollLeft: scrollLeft,
  //         centerTime: null
  //       };
  //     }
  //   }
  // };

  // Restore scroll position after zooming to keep left edge fixed
  // This is called via useEffect when zoomLevelIndex changes
  // COMMENTED OUT: Custom zoom scroll position logic
  // useEffect(() => {
  //   if (scheduleRef.current && scrollPositionRef.current.scrollLeft >= 0) {
  //     // Use setTimeout to ensure the DOM has updated after zoom
  //     const timeoutId = setTimeout(() => {
  //       const scheduleElement = scheduleRef.current?.element;
  //       if (!scheduleElement) return;
  //       
  //       const contentArea = scheduleElement.querySelector('.e-content-wrap') || 
  //                          scheduleElement.querySelector('.e-timeline-view') ||
  //                          scheduleElement.querySelector('.e-content-table');
  //       
  //       if (contentArea) {
  //         // Restore the left edge position (keep it fixed)
  //         const storedScrollLeft = scrollPositionRef.current.scrollLeft;
  //         contentArea.scrollLeft = Math.max(0, storedScrollLeft);
  //         
  //         // Reset the stored position after restoring
  //         scrollPositionRef.current = { scrollLeft: 0, centerTime: null };
  //       }
  //     }, 100); // Delay to ensure DOM is ready after zoom
  //     
  //     return () => clearTimeout(timeoutId);
  //   }
  // }, [zoomLevelIndex]);

  // Zoom functions
  const handleZoomIn = useCallback(() => {
    const currentIndex = zoomLevelIndexRef.current;
    if (currentIndex > 0) {
      // COMMENTED OUT: storeScrollPosition();
      setZoomLevelIndex(prev => prev - 1);
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    const currentIndex = zoomLevelIndexRef.current;
    if (currentIndex < zoomLevels.length - 1) {
      // COMMENTED OUT: storeScrollPosition();
      setZoomLevelIndex(prev => prev + 1);
    }
  }, [zoomLevels.length]);

  const handleZoomReset = useCallback(() => {
    // COMMENTED OUT: storeScrollPosition();
    setZoomLevelIndex(1); // Reset to default (10min, index 1)
  }, []);

  // Get current zoom level
  const currentZoomLevel = zoomLevels[zoomLevelIndex];

  // Functions to scroll to start/end of planning horizon
  const handleScrollToStart = useCallback(() => {
    scheduleViewRef.current?.scrollToStart();
  }, []);

  const handleScrollToEnd = useCallback(() => {
    scheduleViewRef.current?.scrollToEnd();
  }, []);

  // No schedule-specific lifecycle handling needed here (handled inside ProductionScheduleView)


  // Set German locale for Syncfusion Schedule (exactly like ShiftPlanPage)
  useEffect(() => {
    if (i18n.language === 'de') {
      L10n.load({
        'de': {
          schedule: {
            day: 'Tag',
            week: 'Woche',
            workWeek: 'Arbeitswoche',
            month: 'Monat',
            agenda: 'Agenda',
            today: 'Heute',
            noEvents: 'Keine Termine',
            emptyContainer: 'Keine Termine an diesem Tag.',
            allDay: 'Ganztägig',
            start: 'Beginn',
            end: 'Ende',
            more: 'mehr',
            close: 'Schließen',
            cancel: 'Abbrechen',
            noTitle: 'Kein Titel',
            delete: 'Löschen',
            deleteEvent: 'Termin löschen',
            deleteMultipleEvent: 'Mehrere Termine löschen',
            selectedItems: 'Ausgewählte Elemente',
            edit: 'Bearbeiten',
            editSeries: 'Serie bearbeiten',
            editEvent: 'Termin bearbeiten',
            createEvent: 'Erstellen',
            subject: 'Betreff',
            addTitle: 'Titel hinzufügen',
            moreDetails: 'Mehr Details',
            save: 'Speichern',
            editContent: 'Möchten Sie nur diesen Termin oder die gesamte Serie bearbeiten?',
            deleteRecurrenceContent: 'Möchten Sie nur diesen Termin oder die gesamte Serie löschen?',
            deleteContent: 'Sind Sie sicher, dass Sie diesen Termin löschen möchten?',
            deleteMultipleContent: 'Sind Sie sicher, dass Sie die ausgewählten Termine löschen möchten?',
            newEvent: 'Neuer Termin',
            title: 'Titel',
            location: 'Ort',
            description: 'Beschreibung',
            timezone: 'Zeitzone',
            startTimezone: 'Start-Zeitzone',
            endTimezone: 'End-Zeitzone',
            repeat: 'Wiederholen',
            saveButton: 'Speichern',
            cancelButton: 'Abbrechen',
            deleteButton: 'Löschen',
            recurrence: 'Wiederholung',
            none: 'Keine',
            daily: 'Täglich',
            weekly: 'Wöchentlich',
            monthly: 'Monatlich',
            yearly: 'Jährlich',
            never: 'Nie',
            until: 'Bis',
            count: 'Anzahl',
            first: 'Erste',
            second: 'Zweite',
            third: 'Dritte',
            fourth: 'Vierte',
            last: 'Letzte',
            repeatEvery: 'Wiederhole alle',
            weeks: 'Wochen',
            months: 'Monate',
            years: 'Jahre',
            ends: 'Endet',
            on: 'Am',
            after: 'Nach',
            occurrences: 'Vorkommen',
            summaryTimes: 'mal',
            summaryOn: 'am',
            summaryUntil: 'bis',
            summaryRepeat: 'Wiederholt',
            summaryDay: 'Tag(e)',
            summaryWeek: 'Woche(n)',
            summaryMonth: 'Monat(e)',
            summaryYear: 'Jahr(e)',
            editRecurrence: 'Wiederholung bearbeiten',
            deleteRecurrence: 'Wiederholung löschen',
            editOccurrence: 'Vorkommen bearbeiten',
            deleteOccurrence: 'Vorkommen löschen',
            sunday: 'Sonntag',
            monday: 'Montag',
            tuesday: 'Dienstag',
            wednesday: 'Mittwoch',
            thursday: 'Donnerstag',
            friday: 'Freitag',
            saturday: 'Samstag',
            sun: 'So',
            mon: 'Mo',
            tue: 'Di',
            wed: 'Mi',
            thu: 'Do',
            fri: 'Fr',
            sat: 'Sa'
          },
          recurrenceeditor: {
            none: 'Keine',
            daily: 'Täglich',
            weekly: 'Wöchentlich',
            monthly: 'Monatlich',
            yearly: 'Jährlich',
            every: 'Jede',
            days: 'Tag(e)',
            weeks: 'Woche(n)',
            months: 'Monat(e)',
            years: 'Jahr(e)',
            end: 'Ende',
            on: 'Am',
            after: 'Nach',
            occurrences: 'Vorkommen',
            summaryTimes: 'mal',
            summaryOn: 'am',
            summaryUntil: 'bis',
            summaryRepeat: 'Wiederholt',
            summaryDay: 'Tag(e)',
            summaryWeek: 'Woche(n)',
            summaryMonth: 'Monat(e)',
            summaryYear: 'Jahr(e)',
          }
        }
      });
    }
  }, [i18n.language]);

  return (
    <>
      <style>
        {`
          .production-schedule-visualization-dialog .e-schedule {
            border-top: none !important;
          }
          .production-schedule-visualization-dialog .e-schedule-header-container {
            border-top: none !important;
          }
          .production-schedule-visualization-dialog .e-schedule-toolbar-container,
          .production-schedule-visualization-dialog .e-schedule .e-toolbar,
          .production-schedule-visualization-dialog .e-schedule-header-container .e-toolbar {
            display: none !important;
          }
        `}
      </style>
      <DialogComponent
        width="95vw"
        height="95vh"
        visible={isOpen}
        close={handleClose}
        showCloseIcon={false}
        closeOnEscape={true}
        target="#root"
        cssClass="production-schedule-visualization-dialog"
      >
      <div className="p-6 h-full flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {t('productionSchedule.visualization.title')}: {schedule.scheduleName}
          </h2>
        </div>

        {/* KPIs Section */}
        {(() => {
          // Calculate KPI values - use campaigns state if available, otherwise fallback to schedule.campaigns
          const campaignsData = campaigns.length > 0 ? campaigns : (schedule?.campaigns || []);
          const campaignCount = campaignsData.length;
          const doughBatchCount = campaignsData.reduce((sum, campaign) => 
            sum + (campaign.doughBatches?.length ?? 0), 0);
          const ovenBatchCount = campaignsData.reduce((sum, campaign) => 
            sum + (campaign.ovenBatches?.length ?? 0), 0);
          // Count unique products (materials) from campaigns
          const uniqueProducts = new Set(
            campaignsData.map(campaign => campaign.materialId).filter(Boolean)
          );
          const productCount = uniqueProducts.size;

          // Intermediate product orders: in planData.orders those are the ones without a campaignId
          // (transformOrders sets campaignId only when order.getCampaign() != null,
          // which by the phase D schema is true for PRODUCT orders only). Number of distinct
          // intermediate product materials plus number of orders.
          const intermediateOrders = (planData?.orders ?? []).filter((o: any) => !o?.campaignId);
          const intermediateOrderCount = intermediateOrders.length;
          const intermediateProductCount = new Set(
            intermediateOrders.map((o: any) => o?.materialUuid).filter(Boolean)
          ).size;

          return (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <div className="text-sm text-teal-600 font-medium">
                  Anzahl Produkte
                </div>
                <div className="text-2xl font-bold text-teal-800">
                  {productCount}
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-600 font-medium">
                  Anzahl Kampagnen
                </div>
                <div className="text-2xl font-bold text-blue-800">
                  {campaignCount}
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="text-sm text-green-600 font-medium">
                  {t('productionSchedule.visualization.kpis.totalOperations')}
                </div>
                <div className="text-2xl font-bold text-green-800">
                  {planData?.totalOperations ?? 0}
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <div className="text-sm text-purple-600 font-medium">
                  Anzahl Teig-Chargen
                </div>
                <div className="text-2xl font-bold text-purple-800">
                  {doughBatchCount}
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <div className="text-sm text-orange-600 font-medium">
                  Anzahl Ofen-Chargen
                </div>
                <div className="text-2xl font-bold text-orange-800">
                  {ovenBatchCount}
                </div>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <div className="text-sm text-cyan-600 font-medium">
                  Anzahl Vor-Produkte
                </div>
                <div className="text-2xl font-bold text-cyan-800">
                  {intermediateProductCount}
                  {intermediateOrderCount > intermediateProductCount && (
                    <span className="text-sm font-normal text-cyan-600 ml-2">
                      ({intermediateOrderCount} Aufträge)
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Schedule Visualization Section */}
        <div className="flex-1 min-h-0 flex flex-col">
          {/* Custom Toolbar */}
          {!profileLoading && selectedProfile != null && !resourcesLoading && !planDataLoading && !error && resources.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 bg-white border-b border-gray-200">
              <div className="flex items-center gap-2">
                {renderer !== 'capacityChart' && (
                  <>
                    <ButtonComponent
                      cssClass="e-outline e-small"
                      iconCss="e-icons e-arrow-left"
                      onClick={handleScrollToStart}
                      title={t('common.scrollToStart', 'Zum Anfang')}
                    >
                      {t('common.start', 'Anfang')}
                    </ButtonComponent>
                    <ButtonComponent
                      cssClass="e-outline e-small"
                      iconCss="e-icons e-arrow-right"
                      onClick={handleScrollToEnd}
                      title={t('common.scrollToEnd', 'Zum Ende')}
                    >
                      {t('common.end', 'Ende')}
                    </ButtonComponent>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 whitespace-nowrap">
                    {t('productionSchedule.visualization.renderer', 'Darstellung')}:
                  </label>
                  <DropDownListComponent
                    dataSource={[
                      { text: 'Schedule (Timeline)', value: 'schedule' },
                      // { text: 'Gantt', value: 'gantt' },
                      { text: t('productionSchedule.visualization.capacityChart', 'Ressourcen-Auslastung'), value: 'capacityChart' }
                    ]}
                    fields={{ text: 'text', value: 'value' }}
                    value={renderer}
                    change={(e: any) => e?.value && setRenderer(e.value)}
                    width="220px"
                    cssClass="e-small"
                  />
                </div>
                {renderer !== 'capacityChart' && planData && (
                  <div className="flex items-center gap-2 max-w-[min(100%,420px)]">
                    <label className="text-sm text-gray-600 whitespace-nowrap shrink-0">
                      {t('productionSchedule.visualization.sequenceFilter')}:
                    </label>
                    {sequenceFilterHasSlots ? (
                      <DropDownListComponent
                        dataSource={sequenceFilterDataSource}
                        fields={{ text: 'text', value: 'value' }}
                        value={sequenceFilterValue}
                        change={(e: { value?: string }) => {
                          if (e?.value != null) setSequenceFilterValue(String(e.value));
                        }}
                        width="100%"
                        cssClass="e-small"
                      />
                    ) : (
                      <span className="text-xs text-gray-500 leading-tight">
                        {t('productionSchedule.visualization.sequenceSnapshotMissing')}
                      </span>
                    )}
                  </div>
                )}
                {renderer !== 'capacityChart' && (
                  <GanttProfileSelector
                    selectedProfileUuid={selectedProfile?.uuid}
                    onProfileChange={setSelectedProfile}
                  />
                )}
                {renderer !== 'capacityChart' && (
                  <>
                    <ButtonComponent
                      cssClass="e-outline e-small"
                      iconCss="e-icons e-zoom-out"
                      onClick={handleZoomOut}
                      disabled={zoomLevelIndex >= zoomLevels.length - 1}
                      title={t('common.zoomOut', 'Vergrößern')}
                    />
                    <ButtonComponent
                      cssClass="e-outline e-small"
                      onClick={handleZoomReset}
                      title={t('common.zoomReset', 'Zoom zurücksetzen')}
                    >
                      {t('common.reset', 'Reset')}
                    </ButtonComponent>
                    <ButtonComponent
                      cssClass="e-outline e-small"
                      iconCss="e-icons e-zoom-in"
                      onClick={handleZoomIn}
                      disabled={zoomLevelIndex <= 0}
                      title={t('common.zoomIn', 'Verkleinern')}
                    />
                  </>
                )}
              </div>
            </div>
          )}
          {profileLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg">{t('productionSchedule.visualization.loadingProfile', 'Visualisierungsprofil wird geladen...')}</div>
            </div>
          ) : resourcesLoading || planDataLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg">{t('common.loading')}</div>
            </div>
          ) : !selectedProfile ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-lg text-gray-500">{t('gantt.noProfiles', 'Keine Profile verfügbar')}</div>
            </div>
          ) : error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              <strong>{t('common.error')}:</strong> {error}
            </div>
          ) : resources.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-500 text-lg">{t('shiftPlan.noResources')}</div>
            </div>
          ) : renderer === 'capacityChart' ? (
            <div className="flex-1 min-h-0 -mt-px flex flex-col">
              <ResourceUtilizationStepLineChart
                timeWindow={capacityChartTimeWindow}
                operationAssignments={planData?.operationAssignments ?? []}
                additionalResources={planData?.additionalResources ?? []}
                availabilities={availabilities}
                resources={schedulerResourcesWithUnassigned}
                selectedResourceId={capacityChartResourceId}
                onResourceChange={setCapacityChartResourceId}
                height="100%"
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0 -mt-px">
              <ProductionScheduleView
                key={renderer}
                ref={scheduleViewRef}
                renderer={renderer}
                timeWindow={timeWindow}
                now={now}
                operations={filteredOperations}
                setups={filteredSetups}
                resources={schedulerResourcesWithUnassigned}
                zoomLevel={currentZoomLevel.level}
                visualProfile={selectedProfile ?? { name: 'Standard' }}
                onVisualProfileChange={setSelectedProfile}
                onZoomToFit={() => setZoomLevelIndex(zoomLevels.length - 1)}
                width="100%"
                height="100%"
                readonly={true}
              />
            </div>
          )}
        </div>

        {/* Footer with Close Button */}
        <div className="mt-4 flex justify-end">
          <ButtonComponent
            cssClass="e-primary"
            onClick={handleClose}
          >
            {t('actions.close')}
          </ButtonComponent>
        </div>
      </div>
      </DialogComponent>
    </>
  );
};

export default ProductionScheduleVisualizationDialog;
